/** venture/CoTermsFields.tsx — 법인 등록 카드 안의 「수수료 조건」 + 「투자확인서 발급 방식」 (2026-09-24)
 *  기본값이 채워져 있어 표준 조건이면 그대로 두고, 다른 회사만 바꾼다.
 *  방식은 14단계(CertPlanBox)에서도 바꿀 수 있고, 여기서 바꿔도 같은 이력 규칙을 탄다. */
import React from 'react';
import { feeExtrasFor, CERT_MODES, defaultFee, isCapitalMode, planModes } from './types';
import type { Co, FeeTerms, FeeItem, CertPlan, CertMode, CapitalRound } from './types';
import { feeOf, certPlanOf, feeTotals, fmtWon } from './coterms';
import { toggleCertMode } from './certplan';

interface Props { co: Co; onPatch: (patch: Partial<Co>) => void; }

function Seg<T extends string>({ value, opts, onPick }: { value: T; opts: [T, string][]; onPick: (v: T) => void }) {
  return (
    <span className="seg">
      {opts.map(([v, l]) => <button type="button" key={v} className={value === v ? 'on' : ''} onClick={() => onPick(v)}>{l}</button>)}
    </span>
  );
}
const fmtInput = (v: string): string => { const d = v.replace(/[^0-9]/g, ''); return d ? parseInt(d, 10).toLocaleString('ko-KR') : ''; };

export default function CoTermsFields({ co, onPatch }: Props) {
  const fee = feeOf(co);
  const plan = certPlanOf(co);
  const setFee = (patch: Partial<FeeTerms>) => onPatch({ fee: { ...fee, ...patch } });
  const setExtra = (k: 'gpEdu' | 'lawyer' | 'govFee', patch: Partial<FeeItem>) => setFee({ [k]: { ...fee[k], ...patch } } as Partial<FeeTerms>);
  const extras = feeExtrasFor(co);   // LP 회사는 GP교육 수수료 줄 없음(2026-10-18)
  const totals = feeTotals(fee, extras);
  const setPlan = (patch: Partial<CertPlan>) => onPatch({ certPlan: { ...plan, ...patch } });
  const modes = planModes(plan);
  const pickMode = (m: CertMode) => onPatch({ certPlan: toggleCertMode(plan, m, '등록정보에서 변경') });   // 복수 선택 — 켜고 끄기
  const rounds: CapitalRound[] = plan.capital.rounds.length ? plan.capital.rounds : [{ date: '', amount: '', taxYear: '' }];
  const setRound = (i: number, patch: Partial<CapitalRound>) => setPlan({ capital: { rounds: rounds.map((r, j) => (j === i ? { ...r, ...patch } : r)) } });
  const yearNow = new Date().getFullYear();

  return (
    <div className="terms">
      <div className="terms-h"><b>수수료 조건</b>
        <span className="s2tag">청구 예정 {fmtWon(totals.grand)}{totals.vat ? ' (부가세 ' + fmtWon(totals.vat) + ' 포함)' : ''}</span>
        <span className="grow" />
        <button type="button" className="rbtn" onClick={() => onPatch({ fee: defaultFee() })}>기본값으로</button>
      </div>
      {/* 표 형식 — 열 너비가 고정이라 줄이 갈라지지 않고, 선택 버튼 폭이 열마다 같다 (2026-09-24) */}
      <div className="tgrid">
        <div className="tg-h"><span>항목</span><span>청구 방식</span><span>금액</span><span>시기</span><span>부가세</span><span>청구처</span></div>
        <div className="tg-r main">
          <span className="tl">벤처 수수료</span>
          <span className="s3muted">본 수수료</span>
          <span className="amtc"><input className="cotext amt" value={fee.amount} onChange={(e) => setFee({ amount: fmtInput(e.target.value) })} placeholder="3,000,000" inputMode="numeric" /><span className="tu">원</span></span>
          <Seg value={fee.timing} opts={[['post', '후불'], ['pre', '선불'], ['split', '분할']]} onPick={(v) => setFee({ timing: v })} />
          <Seg value={fee.vat} opts={[['separate', '별도'], ['included', '포함']]} onPick={(v) => setFee({ vat: v })} />
          <Seg value={fee.billTo} opts={[['company', '업체'], ['fp', 'FP 대납']]} onPick={(v) => setFee({ billTo: v })} />
        </div>
        {extras.map(([k, label]) => {
          const it = fee[k];
          const sep = it.mode === 'separate';
          return (
            <div className={'tg-r' + (sep ? '' : ' off')} key={k}>
              <span className="tl">{label}</span>
              <Seg value={it.mode} opts={[['separate', '별도 청구'], ['included', '포함']]} onPick={(v) => setExtra(k, { mode: v })} />
              <span className="amtc">{sep
                ? <><input className="cotext amt" value={it.amount} onChange={(e) => setExtra(k, { amount: fmtInput(e.target.value) })} placeholder={k === 'gpEdu' ? '300,000' : '확정 시 입력'} inputMode="numeric" /><span className="tu">원</span></>
                : <span className="s3muted">벤처 수수료에 포함</span>}</span>
              <span className="s3muted">{sep ? '수수료와 같이' : '—'}</span>
              <span className="s3muted">{sep ? '실비' : '—'}</span>
              {sep ? <Seg value={it.billTo} opts={[['company', '업체'], ['fp', 'FP 대납']]} onPick={(v) => setExtra(k, { billTo: v })} /> : <span className="s3muted">—</span>}
            </div>
          );
        })}
      </div>
      <div className="terms-row"><span className="tl">비고</span><input className="cotext" value={fee.memo} onChange={(e) => setFee({ memo: e.target.value })} placeholder="예: 잔금은 확인서 수령 후 7일 내" /></div>

      <div className="terms-h" style={{ marginTop: 14 }}><b>투자확인서 발급 방식</b><span className="s2tag">복수 선택 가능 · 벤처인증 후 14단계에서도 변경 가능</span>{modes.length > 1 ? <span className="pill p-blue">확인서 {modes.length}장</span> : null}</div>
      <div className="modes">
        {CERT_MODES.map(([m, l, d]) => (
          <button type="button" key={m} className={'mode' + (modes.indexOf(m) >= 0 ? ' on' : '')} onClick={() => pickMode(m)}>
            <b>{modes.indexOf(m) >= 0 ? '☑ ' : '☐ '}{l}</b><span>{d}</span>
          </button>
        ))}
      </div>
      {modes.some(isCapitalMode) ? (
        <div className="rounds">
          {rounds.map((r, i) => (
            <div className="terms-row" key={i}>
              <span className="tl">{modes.indexOf('capital_new') >= 0 && modes.indexOf('capital_inc') < 0 ? '설립 출자' : (i + 1) + '회차 증자'}</span>
              <input className="cotext" type="date" value={r.date} onChange={(e) => setRound(i, { date: e.target.value })} style={{ maxWidth: 150 }} />
              <input className="cotext amt" value={r.amount} onChange={(e) => setRound(i, { amount: fmtInput(e.target.value) })} placeholder="증자금액" inputMode="numeric" />
              <span className="tu">원</span>
              <span className="tl" style={{ minWidth: 0 }}>귀속연도</span>
              <select className="cotext" value={r.taxYear} onChange={(e) => setRound(i, { taxYear: e.target.value })} style={{ maxWidth: 110 }}>
                <option value="">선택</option>
                {[yearNow - 2, yearNow - 1, yearNow, yearNow + 1, yearNow + 2].map((y) => <option key={y} value={String(y)}>{y}년</option>)}
              </select>
              {rounds.length > 1 ? <button type="button" className="rbtn danger" onClick={() => setPlan({ capital: { rounds: rounds.filter((_, j) => j !== i) } })}>삭제</button> : null}
            </div>
          ))}
          <button type="button" className="rbtn" onClick={() => setPlan({ capital: { rounds: [...rounds, { date: '', amount: '', taxYear: '' }] } })}>+ 회차 추가</button>
          <div className="snote" style={{ marginTop: 4 }}>같은 해 두 번 증자해도 회차마다 귀속연도를 따로 고를 수 있습니다(출자일부터 2년 내 연도).</div>
        </div>
      ) : null}
      {modes.indexOf('cb_direct') >= 0 ? (
        <div className="terms-row">
          <span className="tl">재인수 예정자</span>
          <input className="cotext" value={plan.direct.buyer} onChange={(e) => setPlan({ direct: { ...plan.direct, buyer: e.target.value } })} placeholder={co.ceo ? co.ceo + ' (대표자)' : '대표자명'} style={{ maxWidth: 160 }} />
          <span className="tl" style={{ minWidth: 0 }}>예정 시기</span>
          <input className="cotext" value={plan.direct.when} onChange={(e) => setPlan({ direct: { ...plan.direct, when: e.target.value } })} placeholder="예: 벤처확인 후 1개월 내" />
        </div>
      ) : null}
    </div>
  );
}
